import type { CSSProperties } from 'react';

/**
 * The reader's text-size setting reaches article CSS two ways, because
 * publisher styles mix relative and absolute units:
 *
 * - `font-size: <n>%` on the renderer's root element. Every `em` size (all
 *   `pt` values, which `CSSBuilder` converts to `em`) follows it for free.
 * - `--article-font-scale` on that same element. `px`/`rem` sizes don't
 *   follow the root, so `CSSBuilder` wraps them in `calc(... * var())`
 *   instead. The fallback of 1 keeps them unscaled where the variable is
 *   never set.
 *
 * Scaling both through one root and one variable means a size is scaled
 * exactly once: an `em` child of an `em` parent inherits the scale instead
 * of compounding it, and an absolute size never sees the root at all.
 */
export const ARTICLE_FONT_SCALE_VAR = '--article-font-scale';

/**
 * The root `font-size` (in %) that renders publisher sizes as authored. At
 * 62.5% of a 16px body, 1em is ~10px, which `PT_TO_EM_FACTOR` is tuned
 * against (1pt ≈ 1px). The reader's setting is stored in the same unit.
 */
export const DEFAULT_ARTICLE_FONT_SIZE = 62.5;

/**
 * The reader's line-spacing multiplier. Set once on <html> by the host
 * (`applyLineHeightScale`), not per renderer: it's a global reading
 * preference, and every line height the renderer emits is multiplied by it.
 * The fallback of 1 keeps publisher values as authored when it's unset.
 */
export const ARTICLE_LINE_HEIGHT_SCALE_VAR = '--article-line-height-scale';

/**
 * The reader's font override. Every publisher font family the renderer
 * emits is `var(--article-font-family, '<publisher font>')`, so the host
 * sets this once on <html> to replace them all; unset, publisher fonts
 * apply as authored.
 */
export const ARTICLE_FONT_FAMILY_VAR = '--article-font-family';

/**
 * What publisher-justified text renders as. Emitted as
 * `var(--article-justify, justify)` wherever a style says `justify`, so a
 * host setting it to `start` turns justified text ragged-right without
 * touching centred or right-aligned text.
 */
export const ARTICLE_JUSTIFY_VAR = '--article-justify';

/**
 * Set to a weight (700) alongside `ARTICLE_FONT_FAMILY_VAR`. Publishers
 * often get bold from the font file itself (`Roboto-Bold`) rather than
 * `font-weight`, so swapping the family would silently drop it.
 */
export const ARTICLE_FONT_BOLD_VAR = '--article-font-bold';

/** Set to `italic` alongside `ARTICLE_FONT_FAMILY_VAR`; see `overridableSyntheticReset`. */
export const ARTICLE_FONT_ITALIC_VAR = '--article-font-italic';

const BOLD_FONT_NAME = /bold|black|heavy/i;

/**
 * The `normal` a publisher's bold/bold-italic rules reset weight or style
 * to, since their font file already draws it bold or italic. Under a font
 * override that file is gone, so the reset gives way to real bold/italic.
 */
export function overridableSyntheticReset(kind: 'bold' | 'italic'): string {
	return kind === 'bold'
		? `var(${ARTICLE_FONT_BOLD_VAR}, normal)`
		: `var(${ARTICLE_FONT_ITALIC_VAR}, normal)`;
}

/**
 * A publisher `font-family` declaration, overridable by
 * `ARTICLE_FONT_FAMILY_VAR`. A family whose name says it's bold also gets
 * `font-weight: var(--article-font-bold, inherit)`: `inherit` is what an
 * unset `font-weight` does anyway, so nothing changes until an override
 * sets the variable.
 */
export function overridableFontFamily(family: string): string {
	const declaration = `font-family: var(${ARTICLE_FONT_FAMILY_VAR}, '${family}');`;
	return BOLD_FONT_NAME.test(family)
		? `${declaration} font-weight: var(${ARTICLE_FONT_BOLD_VAR}, inherit);`
		: declaration;
}

/** A publisher text-align value, with `justify` overridable by `ARTICLE_JUSTIFY_VAR`. */
export function overridableTextAlign(align: string): string {
	return align === 'justify' ? `var(${ARTICLE_JUSTIFY_VAR}, justify)` : align;
}

/**
 * A publisher line height with the reader's line spacing applied, and the
 * text-size scale too when the unit is absolute (see `scaleLength`).
 * Relative units (`em`, `%`, unitless) already follow the text size, so only
 * line spacing multiplies them.
 */
export function scaleLineHeight(value: string | number, unit: string): string {
	const length = isAbsoluteFontUnit(unit)
		? `${value}${unit} * var(${ARTICLE_FONT_SCALE_VAR}, 1)`
		: `${value}${unit}`;
	return `calc(${length} * var(${ARTICLE_LINE_HEIGHT_SCALE_VAR}, 1))`;
}

/** Wraps an absolute length so it follows the reader's text-size setting. */
export function scaleLength(value: string | number, unit: string): string {
	return `calc(${value}${unit} * var(${ARTICLE_FONT_SCALE_VAR}, 1))`;
}

/** `px`/`rem` don't follow the article root's `font-size`, so they need `scaleLength`. */
export function isAbsoluteFontUnit(unit: string | undefined): boolean {
	return unit === 'px' || unit === 'rem';
}

/** The root element's style for a given text-size setting (see `ARTICLE_FONT_SCALE_VAR`). */
export function articleFontSizeStyle(fontSize: number | undefined): CSSProperties {
	if (!fontSize) {
		return {};
	}
	return {
		fontSize: `${fontSize}%`,
		[ARTICLE_FONT_SCALE_VAR]: fontSize / DEFAULT_ARTICLE_FONT_SIZE,
	} as CSSProperties;
}
